import type { FC } from "react";
import { useEffect } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import Select from "@mui/material/Select";

import {
  getRouteFormProperty,
  setRouteFormProperties,
  setRouteFormProperty,
} from "../../../../slices/routes";
import { useDispatch, useSelector } from "../../../../store";
import {
  RouteType,
  protocolToRouteType,
  routeTypeToProtocol,
} from "../../../../utils/route-type";
import { useIsConstrainedCluster } from "@/hooks/use-is-constrained-cluster";

type Props = {
  readonly: boolean;
};

const RouteTypeField: FC<Props> = ({ readonly }) => {
  const dispatch = useDispatch();
  const isConstrainedHostedCluster = useIsConstrainedCluster();
  const from = useSelector(getRouteFormProperty("from", "")) as string;
  const mcp = useSelector(getRouteFormProperty("mcp", null)) as {
    client?: unknown;
    server?: unknown;
  } | null;
  const upstreamTunnel = useSelector(
    getRouteFormProperty("upstreamTunnel", null),
  );

  const currentProtocol = from.includes("://")
    ? from.substring(0, from.indexOf("://") + 3)
    : "https://";

  // Get the actual store value
  const storeRouteType = useSelector(
    getRouteFormProperty(
      "routeType",
      protocolToRouteType(currentProtocol, mcp),
    ),
  ) as RouteType;

  // Display MCP for constrained hosted clusters (may differ from store temporarily)
  const routeType = isConstrainedHostedCluster ? "MCP" : storeRouteType;

  // Initialize SSH tunneling for constrained hosted clusters with MCP routes
  useEffect(() => {
    if (!isConstrainedHostedCluster) {
      return;
    }

    const properties: Array<{ path: string; value: unknown }> = [];

    // Set routeType to MCP if it's not already
    if (storeRouteType !== "MCP") {
      properties.push({ path: "routeType", value: "MCP" });
    }

    // Initialize MCP properties only if neither client nor server is set
    // This respects existing choices (client vs server) when loading routes
    if (!mcp || (!mcp.client && !mcp.server)) {
      // Default to server for new routes
      properties.push(
        { path: "mcp.client", value: "" },
        { path: "mcp.server", value: {} },
      );
    }

    // Only add upstreamTunnel if it's not already set
    if (!upstreamTunnel) {
      properties.push({ path: "upstreamTunnel", value: {} });
    }

    // Ensure the protocol is https:// for MCP
    const [, fqdn] = from.split("://");
    if (from && !from.startsWith("https://")) {
      properties.push({
        path: "from",
        value: "https://" + (fqdn || ""),
      });
    }

    // Only dispatch if there are actual updates to make
    if (properties.length > 0) {
      dispatch(setRouteFormProperties(properties));
    }
  }, [
    isConstrainedHostedCluster,
    storeRouteType,
    upstreamTunnel,
    mcp,
    from,
    dispatch,
  ]);

  const changeRouteType = (event: SelectChangeEvent) => {
    const newRouteType = event.target.value as RouteType;
    const newProtocol = routeTypeToProtocol(newRouteType);

    dispatch(
      setRouteFormProperty({
        path: "routeType",
        value: newRouteType,
      }),
    );

    const [, fqdn] = from.split("://");
    dispatch(
      setRouteFormProperty({
        path: "from",
        value: newProtocol + (fqdn || ""),
      }),
    );

    if (newRouteType === "MCP") {
      const properties = [
        { path: "mcp.client", value: "" },
        { path: "mcp.server", value: {} },
      ];

      // Enable SSH tunneling for hosted clusters
      if (isConstrainedHostedCluster) {
        properties.push({ path: "upstreamTunnel", value: {} });
      }

      dispatch(setRouteFormProperties(properties));
    } else if (routeType === "MCP") {
      const properties: { path: string; value: undefined | null }[] = [
        { path: "mcp", value: null },
      ];

      // Disable SSH tunneling when switching away from MCP in hosted clusters
      if (isConstrainedHostedCluster) {
        properties.push({ path: "upstreamTunnel", value: undefined });
      }

      dispatch(setRouteFormProperties(properties));
    }

    // Clear tunneling settings when switching to UDP
    if (newRouteType === "UDP") {
      dispatch(
        setRouteFormProperty({
          path: "upstreamTunnel",
          value: undefined,
        }),
      );
    }
  };

  const menuItems = isConstrainedHostedCluster
    ? ["MCP"]
    : ["WEB", "SSH", "MCP", "TCP", "UDP"];

  return (
    <Select
      fullWidth
      sx={{ height: 40 }}
      disabled={readonly || isConstrainedHostedCluster}
      labelId="route-type-selector"
      id="route-type-selector"
      value={routeType}
      label="Route Type"
      onChange={changeRouteType}
      MenuProps={{
        disableScrollLock: true,
        PaperProps: {
          style: {
            maxHeight: 300,
          },
        },
      }}
    >
      {menuItems.map((type) => (
        <MenuItem key={type} value={type}>
          {type}
        </MenuItem>
      ))}
    </Select>
  );
};

export default RouteTypeField;
